import { Link } from 'react-router-dom';
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { addDays } from 'date-fns';
import { Calendar, CheckCircle2, AlertTriangle, Plus, Link2, Footprints, CalendarDays, Building } from 'lucide-react';
import { Card, Button } from '../components';
import { AgendaMatrix, dagenVoor, type MatrixConcept, type MatrixWeergave } from '../components/agendaMatrix/AgendaMatrix';
import { PlanPopup } from '../components/agendaMatrix/PlanPopup';
import { MatrixDetail } from '../components/agendaMatrix/MatrixDetail';
import { CalamiteitDialoog } from '../components/agendaMatrix/CalamiteitDialoog';
import * as agenda from '../services/agendaService';
import type { AgendaAfspraak } from '../services/agendaService';

function tijdLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
}

function datumTijdLabel(iso: string): string {
  return new Date(iso).toLocaleString('nl-NL', {
    weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

function duurLabel(minuten: number): string {
  const uren = Math.floor(minuten / 60);
  const rest = minuten % 60;
  if (uren && rest) return `${uren} u ${rest} min`;
  if (uren) return `${uren} uur`;
  return `${rest} min`;
}

interface ProfessionalAgendaPageProps {
  /** Ingebed in de cockpit: geen eigen paginakop en geen buitenmarge. */
  embedded?: boolean;
}

/**
 * 📅 Mijn Agenda Center (onderdeel 56C): Outlook-stijl matrix met alle
 * tijdsdomeinen in de BeLagom-kleurensleutel. Een periode trekken opent de
 * pop-up [ ➕ Wat wilt u inplannen voor deze periode? ].
 */
export const ProfessionalAgendaPage: React.FC<ProfessionalAgendaPageProps> = ({ embedded = false }) => {
  const { t } = useTranslation('agenda');
  const queryClient = useQueryClient();

  const [weergave, setWeergave] = useState<MatrixWeergave>('week');
  const [anker, setAnker] = useState(() => new Date());
  const [concept, setConcept] = useState<MatrixConcept | null>(null);
  const [geselecteerdId, setGeselecteerdId] = useState<string | null>(null);
  const [calamiteitOpen, setCalamiteitOpen] = useState(false);

  const venster = useMemo(() => {
    const dagen = dagenVoor(weergave, anker);
    return { vanaf: dagen[0].toISOString(), tot: addDays(dagen[dagen.length - 1], 1).toISOString() };
  }, [weergave, anker]);

  const matrixQuery = useQuery({
    queryKey: ['agenda-matrix', venster.vanaf, venster.tot],
    queryFn: () => agenda.getMatrix(venster.vanaf, venster.tot),
    placeholderData: (vorige) => vorige,
  });

  // Administratieve afhandeling: verstreken sessies van de laatste 30 dagen
  const vanaf30 = useMemo(() => new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString(), []);
  const afsprakenQuery = useQuery({
    queryKey: ['agenda-afspraken', 'verstreken'],
    queryFn: () => agenda.getAfspraken(vanaf30, new Date().toISOString()),
  });
  const dossiersQuery = useQuery({ queryKey: ['agenda-dossiers'], queryFn: agenda.getAgendaDossiers });

  const ververs = () => {
    queryClient.invalidateQueries({ queryKey: ['agenda-matrix'] });
    queryClient.invalidateQueries({ queryKey: ['agenda-slots'] });
    queryClient.invalidateQueries({ queryKey: ['agenda-afspraken'] });
    queryClient.invalidateQueries({ queryKey: ['agenda-dossiers'] });
    queryClient.invalidateQueries({ queryKey: ['agenda-blokkades'] });
    queryClient.invalidateQueries({ queryKey: ['professional-b2b'] });
  };

  const voltooidMutation = useMutation({
    mutationFn: (id: string) => agenda.registreerVoltooid(id),
    onSuccess: (data) => {
      toast.success(t('afspraak.voltooidBevestiging', { count: data.sessies_verbruikt, defaultValue: 'Sessie {{count}} afgerond' }));
      if (data.verlenging_nodig) {
        toast(t('afspraak.plafondBereikt', { defaultValue: 'Sessieplafond bereikt — vraag verlenging aan bij HR.' }));
      }
      ververs();
    },
    onError: () => toast.error(t('fout.voltooien', { defaultValue: 'Afronden mislukt' })),
  });

  const noShowMutation = useMutation({
    mutationFn: (id: string) => agenda.registreerNoShow(id),
    onSuccess: (data) => {
      toast.success(
        t('afspraak.noShowBevestiging', {
          uren: data.aantal_uren,
          defaultValue: 'No-show geregistreerd. {{uren}} uur zonder platform-opslag op de verzamelfactuur.',
        })
      );
      ververs();
    },
    onError: () => toast.error(t('fout.noShow', { defaultValue: 'Registreren mislukt' })),
  });

  const linkMutation = useMutation({
    mutationFn: (dossierId: string) => agenda.getBoekingsLink(dossierId),
    onSuccess: async (data) => {
      const url = `${window.location.origin}/plannen/${data.token}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success(t('link.gekopieerd', { defaultValue: 'Planlink gekopieerd' }));
      } catch {
        toast.success(url);
      }
    },
    onError: () => toast.error(t('fout.link', { defaultValue: 'Link genereren mislukt' })),
  });

  const selecteer = (start: Date, eind: Date) => {
    if (eind.getTime() <= Date.now()) {
      toast.error(t('matrix.verleden', 'Deze periode ligt in het verleden.'));
      return;
    }
    setGeselecteerdId(null);
    setConcept({ start, eind, kleur: null });
  };

  const nieuwOpVolgendUur = () => {
    const start = new Date();
    start.setMinutes(0, 0, 0);
    start.setHours(start.getHours() + 1);
    setConcept({ start, eind: new Date(start.getTime() + 60 * 60 * 1000), kleur: null });
  };

  const items = matrixQuery.data ?? [];
  const geselecteerd = items.find((i) => i.id === geselecteerdId) ?? null;
  const nu = Date.now();
  const verstreken = (afsprakenQuery.data ?? []).filter((a) => a.status === 'SCHEDULED' && new Date(a.start_tijd).getTime() <= nu);

  return (
    <div className={embedded ? 'space-y-6' : 'max-w-7xl mx-auto px-4 py-8 space-y-6'}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        {embedded ? (
          <p className="text-sm text-gray-600">
            {t('matrix.subtitel', 'Sleep in de week- of dagweergave over een periode om tijd open te zetten, een sessie of overleg te plannen of tijd af te schermen.')}
          </p>
        ) : (
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Calendar className="w-6 h-6" />
              {t('matrix.titel', 'Mijn Agenda Center')}
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {t('matrix.subtitel', 'Sleep in de week- of dagweergave over een periode om tijd open te zetten, een sessie of overleg te plannen of tijd af te schermen.')}
            </p>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button variant="danger" onClick={() => setCalamiteitOpen(true)}>
            {t('matrix.calamiteit.knop', '🤒 Acute calamiteit / ziek melden')}
          </Button>
          {/* Route 3 van de registratie-poort: gesprek zonder afspraak vooraf */}
          <Link to="/professional/adhoc-melding">
            <Button variant="outline">
              <Footprints className="w-4 h-4 mr-2" />
              {t('adhocMelding', { defaultValue: 'Ad-hoc melding registreren' })}
            </Button>
          </Link>
          <Button onClick={nieuwOpVolgendUur}>
            <Plus className="w-4 h-4 mr-2" />
            {t('matrix.inplannen', 'Inplannen')}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-4 items-start">
        <AgendaMatrix
          items={items}
          weergave={weergave}
          anker={anker}
          onWeergave={setWeergave}
          onAnker={setAnker}
          onItemClick={(i) => {
            setConcept(null);
            setGeselecteerdId(i.id);
          }}
          onSelectie={selecteer}
          concept={concept}
          geselecteerdId={geselecteerdId}
          laden={matrixQuery.isFetching}
        />
        <div className="space-y-4">
          {geselecteerd ? (
            <MatrixDetail key={geselecteerd.id} item={geselecteerd} onSluiten={() => setGeselecteerdId(null)} onGewijzigd={ververs} />
          ) : (
            <div className="bg-navy-50/60 rounded-xl border border-navy-100 p-6 text-sm text-navy-700 leading-relaxed">
              <CalendarDays className="w-12 h-12 text-navy-900 mb-4 mx-auto" strokeWidth={1.5} />
              {t('matrix.hint', 'Klik op een blok voor details en acties. Sleep over lege of open tijd om iets nieuws in te plannen. Intervisie, opleidingen en events laden automatisch in.')}
            </div>
          )}
          {/* B2B-agenda (migratie 132): lijstweergave met alle afspraken met opdrachtgevers */}
          <Link
            to="/professional/agenda/opdrachtgevers"
            className="flex items-center gap-2 rounded-lg border border-gold-200 bg-gold-50 px-4 py-3 text-sm text-navy-900 hover:bg-gold-100"
          >
            <Building className="w-4 h-4 flex-shrink-0" />
            {t('matrix.b2bOverzicht', 'Alle afspraken met opdrachtgevers →')}
          </Link>
        </div>
      </div>

      {/* Verstreken afspraken — administratieve afhandeling */}
      {verstreken.length > 0 && (
        <Card className="p-6">
          <h2 className="font-semibold text-gray-900 mb-4">{t('afhandelen.titel', { defaultValue: 'Af te handelen' })}</h2>
          <div className="space-y-3">
            {verstreken.map((a) => (
              <AfspraakRij
                key={a.id}
                afspraak={a}
                acties={
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => voltooidMutation.mutate(a.id)} disabled={voltooidMutation.isPending}>
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      {t('afspraak.voltooid', { defaultValue: 'Registreer als voltooid' })}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => noShowMutation.mutate(a.id)} disabled={noShowMutation.isPending}>
                      <AlertTriangle className="w-4 h-4 mr-1" />
                      {t('afspraak.noShow', { defaultValue: 'Registreer no-show' })}
                    </Button>
                  </div>
                }
              />
            ))}
          </div>
        </Card>
      )}

      {/* Dossiers + planlinks */}
      <Card className="p-6">
        <h2 className="font-semibold text-gray-900 mb-4">{t('dossiers.titel', { defaultValue: 'Trajecten en planlinks' })}</h2>
        {(dossiersQuery.data ?? []).length === 0 ? (
          <p className="text-sm text-gray-500">{t('dossiers.leeg', { defaultValue: 'Geen actieve trajecten.' })}</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {(dossiersQuery.data ?? []).map((d) => (
              <div key={d.id} className="py-3 flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <p className="font-mono text-sm text-gray-900">{d.client_registration_number || d.id.slice(0, 8)}</p>
                  <p className="text-xs text-gray-600">
                    {t(`sessieType.${d.volgende_sessie_type}`, {
                      defaultValue: d.volgende_sessie_type === 'intake' ? 'Intake' : 'Vervolggesprek',
                    })}{' '}
                    · {duurLabel(d.duur_minuten)}
                    {d.max_sessies !== null && ` · ${d.sessies_verbruikt}/${d.max_sessies} sessies`}
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={() => linkMutation.mutate(d.id)} disabled={d.plafond_bereikt || linkMutation.isPending}>
                  <Link2 className="w-4 h-4 mr-1" />
                  {t('dossiers.planlink', { defaultValue: 'Planlink kopiëren' })}
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {concept && (
        <PlanPopup
          start={concept.start}
          eind={concept.eind}
          onKleur={(kleur) => setConcept((c) => (c ? { ...c, kleur } : c))}
          onPeriode={(start, eind) => setConcept((c) => (c ? { ...c, start, eind } : c))}
          onSluiten={() => setConcept(null)}
          onGepland={() => {
            setConcept(null);
            ververs();
          }}
        />
      )}
      {calamiteitOpen && (
        <CalamiteitDialoog
          onSluiten={() => setCalamiteitOpen(false)}
          onGemeld={() => {
            setCalamiteitOpen(false);
            ververs();
          }}
        />
      )}
    </div>
  );
};

// ── Onderdelen ──────────────────────────────────────────────────────────────

const AfspraakRij: React.FC<{ afspraak: AgendaAfspraak; acties?: React.ReactNode }> = ({ afspraak, acties }) => {
  const { t } = useTranslation('agenda');
  const duur = Math.round((new Date(afspraak.eind_tijd).getTime() - new Date(afspraak.start_tijd).getTime()) / 60000);

  return (
    <div className="flex items-center justify-between gap-4 flex-wrap border border-gray-100 rounded-lg px-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900">
          {datumTijdLabel(afspraak.start_tijd)}–{tijdLabel(afspraak.eind_tijd)}
          <span className="text-gray-500 font-normal"> · {duurLabel(duur)}</span>
        </p>
        <p className="text-xs text-gray-600 mt-0.5">
          <span className="font-mono">{afspraak.client_registration_number || afspraak.dossier_id.slice(0, 8)}</span>
          {' · '}
          {t(`sessieType.${afspraak.sessie_type}`, {
            defaultValue: afspraak.sessie_type === 'intake' ? 'Intake' : 'Vervolggesprek',
          })}{' '}
          {afspraak.sessie_nummer}
          {' · '}
          {t(`locatie.${afspraak.locatie_type}`, { defaultValue: afspraak.locatie_type })}
          {afspraak.locatie_omschrijving ? ` — ${afspraak.locatie_omschrijving}` : ''}
        </p>
      </div>
      <div className="flex items-center gap-3">
        {/* Registratie-poort (migratie 173): het verslag hangt aan deze afspraak */}
        <Link
          to={`/professional/dossiers/${afspraak.dossier_id}/verslagen/nieuw?afspraak=${afspraak.id}`}
          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
        >
          {t('afspraak.verslag', { defaultValue: 'Verslag' })}
        </Link>
        {acties}
      </div>
    </div>
  );
};

export default ProfessionalAgendaPage;
